import * as React from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { AppHeader } from "../components/AppHeader.tsx";
import {
  ArrowLeftIcon,
  CheckCircleIcon,
  ChevronDownIcon,
  LinkIcon,
  MagnifyingGlassIcon,
} from "../components/icons.tsx";
import { getHelpContent } from "../data/helpCenter";
import type {
  HelpArticle,
  HelpCategoryId,
  HelpSection,
} from "../data/helpCenter.types";

const CATEGORY_BADGE: Record<HelpCategoryId, string> = {
  "getting-started": "bg-emerald-50 text-emerald-700",
  "image-types": "bg-blue-50 text-blue-700",
  "edit-image": "bg-violet-50 text-violet-700",
  "quick-actions": "bg-amber-50 text-amber-700",
  "settings-subscription": "bg-gray-100 text-gray-700",
  "projects-workflows": "bg-rose-50 text-rose-700",
};

function normalizeSearch(value: string): string {
  return value
    .normalize("NFD")
    .replace(/\p{Diacritic}/gu, "")
    .toLowerCase();
}

function articleSearchText(a: HelpArticle): string {
  const parts: string[] = [a.title, a.summary, a.whatFor, a.howToAccess];
  for (const s of a.sections) {
    if (s.heading) parts.push(s.heading);
    if (s.body) parts.push(s.body);
    if (s.steps)
      parts.push(...s.steps.map((st) => `${st.title ?? ""} ${st.body}`));
    if (s.tips) parts.push(...s.tips);
  }
  return normalizeSearch(parts.join(" "));
}

const HelpSectionView: React.FC<{
  section: HelpSection;
  tipsLabel: string;
}> = ({ section, tipsLabel }) => (
  <div className="space-y-2">
    {section.heading && (
      <h4 className="text-sm font-semibold text-gray-900">{section.heading}</h4>
    )}
    {section.body && (
      <p className="text-sm leading-relaxed text-gray-700">{section.body}</p>
    )}
    {section.steps && (
      <ol className="space-y-2">
        {section.steps.map((step, i) => (
          <li key={i} className="flex gap-3 text-sm text-gray-700">
            <span className="mt-0.5 inline-flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full bg-gray-900 text-[11px] font-semibold text-white">
              {i + 1}
            </span>
            <span className="leading-relaxed">
              {step.title && (
                <span className="font-semibold text-gray-900">
                  {step.title}:{" "}
                </span>
              )}
              {step.body}
            </span>
          </li>
        ))}
      </ol>
    )}
    {section.tips && (
      <div className="rounded-lg bg-gray-50 p-3">
        <p className="mb-2 text-[10px] font-semibold uppercase tracking-widest text-gray-400">
          {tipsLabel}
        </p>
        <ul className="space-y-1.5">
          {section.tips.map((tip, i) => (
            <li key={i} className="flex gap-2 text-sm text-gray-700">
              <span className="text-emerald-500">✓</span>
              <span className="leading-relaxed">{tip}</span>
            </li>
          ))}
        </ul>
      </div>
    )}
    {section.screenshot && (
      <img
        src={section.screenshot}
        alt={section.screenshotAlt ?? ""}
        loading="lazy"
        className="mt-2 w-full rounded-lg border border-gray-200"
        onError={(e) => {
          e.currentTarget.style.display = "none";
        }}
      />
    )}
  </div>
);

const HelpArticleCard: React.FC<{
  article: HelpArticle;
  categoryLabel: string;
  badgeClass: string;
  isOpen: boolean;
  onToggle: () => void;
  relatedItems: { id: string; title: string }[];
  onOpenRelated: (id: string) => void;
}> = ({
  article,
  categoryLabel,
  badgeClass,
  isOpen,
  onToggle,
  relatedItems,
  onOpenRelated,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [justCopied, setJustCopied] = React.useState(false);

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(
        `${window.location.origin}/help#${article.id}`
      );
      setJustCopied(true);
      toast.success(t("helpCenter.linkCopied"));
      window.setTimeout(() => setJustCopied(false), 2000);
    } catch {
      toast.error(t("helpCenter.copyFailed"));
    }
  };

  return (
    <div
      id={article.id}
      className="scroll-mt-24 rounded-xl border border-gray-200 bg-white"
    >
      <div className="flex w-full items-start gap-2 p-5">
        <button
          type="button"
          onClick={onToggle}
          aria-expanded={isOpen}
          className="flex min-w-0 flex-1 items-start justify-between gap-4 text-left"
        >
          <div className="min-w-0">
            <div className="mb-1 flex flex-wrap items-center gap-2">
              <h3 className="text-base font-semibold text-gray-900">
                {article.title}
              </h3>
              <span
                className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${badgeClass}`}
              >
                {categoryLabel}
              </span>
              {article.comingSoon && (
                <span className="rounded-full bg-gray-100 px-2 py-0.5 text-[11px] font-semibold text-gray-500">
                  {t("helpCenter.comingSoon")}
                </span>
              )}
            </div>
            <p className="text-sm leading-relaxed text-gray-500">
              {article.summary}
            </p>
          </div>
          <ChevronDownIcon
            className={`mt-1 h-5 w-5 flex-shrink-0 text-gray-400 transition-transform ${
              isOpen ? "rotate-180" : ""
            }`}
          />
        </button>
        <button
          type="button"
          onClick={handleCopyLink}
          title={t("helpCenter.share")}
          aria-label={t("helpCenter.share")}
          className="mt-0.5 flex-shrink-0 rounded-lg p-1.5 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-700"
        >
          {justCopied ? (
            <CheckCircleIcon className="h-4 w-4 text-emerald-500" />
          ) : (
            <LinkIcon className="h-4 w-4" />
          )}
        </button>
      </div>

      {isOpen && (
        <div className="space-y-5 border-t border-gray-100 p-5">
          <div>
            <p className="mb-1 text-[10px] font-semibold uppercase tracking-widest text-gray-400">
              {t("helpCenter.whatFor")}
            </p>
            <p className="text-sm leading-relaxed text-gray-700">
              {article.whatFor}
            </p>
          </div>
          <div>
            <p className="mb-1 text-[10px] font-semibold uppercase tracking-widest text-gray-400">
              {t("helpCenter.howToAccess")}
            </p>
            <p className="text-sm leading-relaxed text-gray-700">
              {article.howToAccess}
            </p>
            {article.pageLink && (
              <button
                type="button"
                onClick={() => navigate({ to: article.pageLink!.to })}
                className="mt-2 inline-flex items-center gap-1.5 rounded-lg bg-gray-900 px-3 py-1.5 text-xs font-semibold text-white transition-colors hover:bg-gray-700"
              >
                {t("helpCenter.openSettingsPage")}
                <span aria-hidden>→</span>
              </button>
            )}
          </div>
          {article.sections.map((section, i) => (
            <HelpSectionView
              key={i}
              section={section}
              tipsLabel={t("helpCenter.tips")}
            />
          ))}
          {relatedItems.length > 0 && (
            <div className="border-t border-gray-100 pt-4">
              <p className="mb-2 text-[10px] font-semibold uppercase tracking-widest text-gray-400">
                {t("helpCenter.related")}
              </p>
              <div className="flex flex-wrap gap-2">
                {relatedItems.map((rel) => (
                  <button
                    key={rel.id}
                    type="button"
                    onClick={() => onOpenRelated(rel.id)}
                    className="rounded-full border border-gray-200 px-3 py-1 text-xs font-semibold text-gray-700 transition-colors hover:border-gray-300 hover:bg-gray-50"
                  >
                    {rel.title} →
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const HelpCenterPage: React.FC = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const content = React.useMemo(
    () => getHelpContent(i18n.language),
    [i18n.language]
  );

  const [activeCategory, setActiveCategory] = React.useState<
    "all" | HelpCategoryId
  >("all");
  const [query, setQuery] = React.useState("");
  const [expandedId, setExpandedId] = React.useState<string | null>(null);

  const articlesById = React.useMemo(() => {
    const map = new Map<string, HelpArticle>();
    for (const a of content.articles) map.set(a.id, a);
    return map;
  }, [content]);

  const scrollToArticle = React.useCallback((id: string) => {
    window.setTimeout(() => {
      document
        .getElementById(id)
        ?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 50);
  }, []);

  // Deep-link: /help#article-id opens and scrolls to that article on mount.
  React.useEffect(() => {
    const hash = window.location.hash.replace(/^#/, "");
    if (hash && articlesById.has(hash)) {
      setExpandedId(hash);
      scrollToArticle(hash);
    }
  }, [articlesById, scrollToArticle]);

  const openArticle = React.useCallback(
    (id: string) => {
      setActiveCategory("all");
      setQuery("");
      setExpandedId(id);
      window.history.replaceState(null, "", `#${id}`);
      scrollToArticle(id);
    },
    [scrollToArticle]
  );

  const normalizedQuery = normalizeSearch(query.trim());
  const matchesQuery = React.useCallback(
    (a: HelpArticle) =>
      normalizedQuery === "" || articleSearchText(a).includes(normalizedQuery),
    [normalizedQuery]
  );

  const visibleGroups = content.categories
    .filter((c) => activeCategory === "all" || c.id === activeCategory)
    .map((category) => ({
      category,
      articles: content.articles.filter(
        (a) => a.category === category.id && matchesQuery(a)
      ),
    }))
    .filter((g) => g.articles.length > 0);

  const totalCount = visibleGroups.reduce(
    (sum, g) => sum + g.articles.length,
    0
  );

  const toggle = (id: string) =>
    setExpandedId((current) => (current === id ? null : id));

  const chipClass = (active: boolean) =>
    `rounded-full px-4 py-1.5 text-sm font-semibold transition-colors ${
      active
        ? "bg-black text-white"
        : "bg-gray-100 text-gray-700 hover:bg-gray-200"
    }`;

  return (
    <>
      <div className="flex h-full w-full flex-col overflow-hidden">
        <AppHeader />
        <div className="flex-1 overflow-y-auto bg-gray-50">
          <div className="mx-auto max-w-5xl px-6 py-10">
            <button
              type="button"
              onClick={() =>
                navigate({ to: "/", search: { returnTab: undefined } })
              }
              className="mb-6 flex items-center gap-2 text-sm text-gray-500 transition-colors hover:text-black"
            >
              <ArrowLeftIcon className="h-4 w-4" /> {t("helpCenter.back")}
            </button>

            <h1 className="text-3xl tracking-tight font-semibold text-gray-900">
              {t("helpCenter.title")}
            </h1>
            <p className="mt-1 text-gray-500">{t("helpCenter.subtitle")}</p>

            <div className="mt-6 flex flex-wrap gap-2">
              <button
                type="button"
                onClick={() => setActiveCategory("all")}
                className={chipClass(activeCategory === "all")}
              >
                {t("helpCenter.allCategories")}
              </button>
              {content.categories.map((c) => (
                <button
                  key={c.id}
                  type="button"
                  onClick={() => setActiveCategory(c.id)}
                  className={chipClass(activeCategory === c.id)}
                >
                  {c.label}
                </button>
              ))}
            </div>

            <div className="relative mt-4">
              <MagnifyingGlassIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
              <input
                type="search"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder={t("helpCenter.searchPlaceholder")}
                className="w-full rounded-lg border border-gray-200 bg-white py-2.5 pl-9 pr-4 text-sm text-gray-900 outline-none focus:border-gray-300"
              />
            </div>

            <p className="mt-4 text-xs font-semibold uppercase tracking-widest text-gray-400">
              {t("helpCenter.articleCount", { count: totalCount })}
            </p>

            <div className="mt-4 space-y-8">
              {visibleGroups.map((group) => (
                <section key={group.category.id}>
                  <div className="mb-3">
                    <h2 className="text-lg font-semibold text-gray-900">
                      {group.category.label}
                    </h2>
                    {group.category.description && (
                      <p className="text-sm text-gray-500">
                        {group.category.description}
                      </p>
                    )}
                  </div>
                  <div className="space-y-3">
                    {group.articles.map((article) => (
                      <HelpArticleCard
                        key={article.id}
                        article={article}
                        categoryLabel={group.category.label}
                        badgeClass={CATEGORY_BADGE[article.category]}
                        isOpen={expandedId === article.id}
                        onToggle={() => toggle(article.id)}
                        relatedItems={(article.related ?? [])
                          .map((id) => articlesById.get(id))
                          .filter((a): a is HelpArticle => Boolean(a))
                          .map((a) => ({ id: a.id, title: a.title }))}
                        onOpenRelated={openArticle}
                      />
                    ))}
                  </div>
                </section>
              ))}

              {totalCount === 0 && (
                <p className="py-12 text-center text-sm text-gray-500">
                  {t("helpCenter.noResults")}
                </p>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export const Route = createFileRoute("/help")({
  component: HelpCenterPage,
});
